import { getIntlLocale } from "utils/getIntlLocale";

export type DeltaStatus = "up" | "down" | "equal";

export interface DeltaPercent {
  delta: string | null;
  status: DeltaStatus | null;
}

export function getDeltaPercent(
  currentValue: number | null | undefined,
  previousValue: number | null | undefined
): DeltaPercent {
  if (currentValue == null || previousValue == null || previousValue === 0) {
    return {
      delta: null,
      status: null,
    };
  }

  const delta = parseFloat(currentValue.toString()) / parseFloat(previousValue.toString());
  let status: DeltaStatus = "equal";

  if (delta > 1) {
    status = "up";
  }

  if (delta < 1) {
    status = "down";
  }

  return {
    delta: delta.toFixed(2),
    status,
  };
}

export function updateMonths(date: string, months: number, isAdd: boolean): Date {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + (isAdd ? months : -months));
  return newDate;
}

export function getMaxAndMin(values: number[]): { max: number; min: number } {
  if (!Array.isArray(values)) {
    throw new Error("The input values must be an array");
  }

  if (!values.length) {
    return {
      max: 100,
      min: 0,
    };
  }

  const orderedValues = [...values].sort((a, b) => a - b);

  const max = orderedValues[orderedValues.length - 1];
  const min = orderedValues[0];
  const padding = Math.max(5, Math.ceil((max - min) * 0.1));

  return {
    max: max + padding,
    min: Math.max(0, min - padding),
  };
}

/** Maps a T-score to the SRS-2 severity range label stored in the schema. */
export function getSrs2Severity(
  tScore: number | null
): "Normal" | "Mild" | "Moderate" | "Severe" | null {
  if (tScore === null) return null;
  if (tScore >= 76) return "Severe";
  if (tScore >= 66) return "Moderate";
  if (tScore >= 60) return "Mild";
  return "Normal";
}

/** Maps the SRS-2 total T-score (sum of 6 subscales) to its aggregate severity range. */
export function getSrs2TotalSeverity(
  tScore: number | null
): "Normal" | "Mild" | "Moderate" | "Severe" | null {
  if (tScore === null) return null;
  if (tScore >= 456) return "Severe";
  if (tScore >= 396) return "Moderate";
  if (tScore >= 360) return "Mild";
  return "Normal";
}

export function formatAssessmentDate(dateString: string | null | undefined): string {
  if (!dateString) {
    return "-";
  }

  const date = parseCalendarDate(dateString);
  if (!date) {
    return "-";
  }

  const formatter = new Intl.DateTimeFormat(getIntlLocale(), {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const formattedDate = formatter.format(date);

  return formattedDate;
}

function parseCalendarDate(value: string): Date | null {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
}

export function formatAssessmentTimestampLocal(
  dateString: string | Date | null | undefined
): string {
  if (!dateString) {
    return "-";
  }

  const date = dateString instanceof Date ? dateString : new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat(getIntlLocale(), {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

// ─── Vineland Score Calculations ─────────────────────────────────────────────

/**
 * Maps a Vineland standard score (ABC or domain) to its adaptive level descriptor.
 * Score ranges: High (130-140), Moderately High (115-129), Adequate (86-114),
 * Moderately Low (71-85), Low (20-70).
 *
 * @param score - The standard score to map. Null means "Not Tested".
 */
export function getVinelandAdaptiveLevel(score: number | null): string | null {
  if (score === null) return null;
  if (score >= 130) return "High";
  if (score >= 115) return "Moderately High";
  if (score >= 86) return "Adequate";
  if (score >= 71) return "Moderately Low";
  if (score >= 20) return "Low";
  return null;
}

/**
 * Maps a Vineland v-scale score to its qualitative descriptor.
 * Score ranges: Clinically Significant (21-24), Elevated (18-20), Average (1-17).
 *
 * @param vScale - The v-scale score to map. Null means "Not Tested".
 */
export function getVinelandQualitativeDescriptor(vScale: number | null): string | null {
  if (vScale === null) return null;
  if (vScale >= 21) return "Clinically Significant";
  if (vScale >= 18) return "Elevated";
  if (vScale >= 1) return "Average";
  return null;
}

export function formatAssessmentChartDateLabel(
  dateString: string | Date | null | undefined
): string {
  if (!dateString) {
    return "-";
  }

  const date =
    dateString instanceof Date
      ? dateString
      : parseCalendarDate(typeof dateString === "string" ? dateString : String(dateString));

  if (!date || Number.isNaN(date.getTime())) {
    return typeof dateString === "string" ? dateString : "-";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date);
}

// ─── AFLS domain-total calculation ───────────────────────────────────────────

/**
 * Computes an AFLS domain (step) total from its sub-skill scores.
 *
 * Shared by the on-screen total (AflsDataEntry) and the API payload
 * (DataEntryDialogContent) so the two can never diverge. Returns `null` when
 * the step is not completed OR when no sub-skill has an entered score — the
 * backend computes `null` for an all-null domain, and sending `0` instead would
 * fail server-side derived-field validation ("provided 0, expected null").
 * When at least one score is entered, unentered items count as 0.
 */
export function aflsStepTotal(step: {
  stepCompleted: boolean;
  skills: { score: number | null }[];
}): number | null {
  if (!step.stepCompleted) return null;
  const hasAny = step.skills.some((sk) => sk.score !== null);
  if (!hasAny) return null;
  return step.skills.reduce((sum, sk) => sum + (sk.score ?? 0), 0);
}
